import { useState, useEffect } from "react";
import { useEmailChannel } from "../hooks/useEmailChannel";
import { useJobTypes } from "../hooks/useJobTypes";
import { toast } from "@/components/Toaster";
import { ConfirmModal } from "@/components/ConfirmModal";

export function EmailIngestionPanel() {
  const { config, isLoading, error, save, remove, generateSecret } = useEmailChannel();
  const { items: jobTypes } = useJobTypes();

  const [ingestAddress, setIngestAddress] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [jobTypeId, setJobTypeId] = useState("");
  const [isEnabled, setIsEnabled] = useState(false);
  // R-266 6a — when true, every inbound email becomes a typeless batch routed
  // by its document fingerprint rather than the configured default job type.
  const [routeByContent, setRouteByContent] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Sync from server config
  useEffect(() => {
    if (config) {
      setIngestAddress(config.ingestAddress);
      setWebhookSecret(config.webhookSecret);
      setJobTypeId(config.jobTypeId);
      setIsEnabled(config.isEnabled);
      setRouteByContent(config.routeByContent ?? false);
    }
  }, [config]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setIsSaving(true);
    try {
      await save({ ingestAddress, webhookSecret, jobTypeId, isEnabled, routeByContent });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateSecret = async () => {
    setIsGenerating(true);
    try {
      const { secret } = await generateSecret();
      setWebhookSecret(secret);
      toast.success("New webhook secret generated. Save the form to apply.");
    } catch (e) {
      // R-69 — alert() was a dead end during bootstrap (no config row -> 404).
      // The route is now idempotent (upsert), so any failure is a real error
      // and the operator can keep working in the panel.
      toast.error(e instanceof Error ? e.message : "Failed to generate secret");
    } finally {
      setIsGenerating(false);
    }
  };

  const [removeOpen, setRemoveOpen] = useState(false);

  const handleRemove = () => setRemoveOpen(true);

  const handleConfirmRemove = async () => {
    setRemoveOpen(false);
    setIsRemoving(true);
    try {
      await remove();
      setIngestAddress("");
      setWebhookSecret("");
      setJobTypeId("");
      setIsEnabled(false);
      setRouteByContent(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to remove");
    } finally {
      setIsRemoving(false);
    }
  };

  if (isLoading) {
    return <div className="admin-loading"><div className="loading-spinner" /></div>;
  }

  const webhookEndpoint = ingestAddress
    ? `${window.location.origin}/api/ingest/email/${encodeURIComponent(ingestAddress)}`
    : null;

  return (
    <div className="panel-content">
      <div className="rb-section">
        <h3 className="rb-section-title">Email Ingestion</h3>
        <p className="rb-section-desc">
          Configure an ingest address and webhook secret to receive documents via email.
          Point your email provider's webhook to the endpoint shown below.
        </p>

        {error && <div className="admin-error">{error}</div>}

        <form onSubmit={handleSave}>
          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Ingest Address</label>
            <input
              className="rb-input"
              value={ingestAddress}
              onChange={(e) => setIngestAddress(e.target.value)}
              placeholder="capture-invoices@ingest.company.com"
              type="email"
            />
          </div>

          <div style={{ marginBottom: 10 }}>
            <label className="rb-label">Webhook Secret</label>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <input
                className="rb-input"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="Shared secret for HMAC verification"
                style={{ flex: 1, fontFamily: "monospace" }}
              />
              <button
                type="button"
                className="rb-btn"
                onClick={handleGenerateSecret}
                disabled={isGenerating}
              >
                {isGenerating ? "…" : "Generate"}
              </button>
            </div>
            {/* WU-9.5 — Bootstrap-order helper. Operators were saving the form
                first (which 400'd on missing ingestAddress) trying to find a
                way to mint the secret. The button is now idempotent, so this
                hint tells them the right order. */}
            <p className="rb-hint" style={{ marginTop: 4 }}>
              First time? Generate a secret here, paste it into your email
              provider's webhook config, then save the rest of the form.
            </p>
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Default Job Type</label>
            <select
              className="rb-select"
              value={jobTypeId}
              onChange={(e) => setJobTypeId(e.target.value)}
            >
              <option value="">Auto-detect</option>
              {jobTypes.map((jt) => (
                <option key={jt.id} value={jt.id}>{jt.name}</option>
              ))}
            </select>
          </div>

          <div className="rb-row" style={{ marginBottom: 10, alignItems: "center", gap: 8 }}>
            <label className="rb-label">Enabled</label>
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
            />
          </div>

          {/* R-266 6a — typeless ingestion: route every email by its document
              fingerprint instead of pinning it to the default job type. */}
          <div className="rb-row" style={{ marginBottom: 4, alignItems: "center", gap: 8 }}>
            <label className="rb-label" htmlFor="email-route-by-content">Route by content</label>
            <input
              id="email-route-by-content"
              type="checkbox"
              checked={routeByContent}
              onChange={(e) => setRouteByContent(e.target.checked)}
            />
          </div>
          <p className="rb-hint" style={{ marginBottom: 16 }}>
            Route by content (no fixed job type) — auto-route every document by its fingerprint.
          </p>

          {webhookEndpoint && (
            <div className="webhook-endpoint-display" style={{ marginBottom: 16 }}>
              <label className="rb-label">Webhook Endpoint</label>
              <div className="endpoint-value" style={{ marginTop: 4 }}>
                <code style={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>
                  {webhookEndpoint}
                </code>
              </div>
            </div>
          )}

          {saveError && <div className="admin-error" style={{ marginBottom: 10 }}>{saveError}</div>}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {config && (
              <button
                type="button"
                className="rb-btn rb-btn-danger"
                onClick={handleRemove}
                disabled={isRemoving}
              >
                {isRemoving ? "Removing…" : "Remove"}
              </button>
            )}
            <button type="submit" className="rb-btn rb-btn-save" disabled={isSaving}>
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
      <ConfirmModal
        open={removeOpen}
        title="Remove email ingestion configuration?"
        description="This cannot be undone. Existing inbound mail will be rejected until you re-configure."
        confirmLabel="Remove"
        destructive
        onConfirm={handleConfirmRemove}
        onCancel={() => setRemoveOpen(false)}
      />
    </div>
  );
}
